import { useEffect, useState } from 'react'
import { TopBar } from '../components/TopBar'
import { Avatar, roleLabels } from '../components/Avatar'
import { useApp } from '../store/AppContext'
import {
  ONBOARDING_REQUIREMENTS,
  getOnboardingRecord,
  loadOnboardingRecords,
  onboardingProgress,
  saveOnboardingRecord,
  type WorkerOnboardingRecord,
} from '../lib/onboarding'
import {
  CREDENTIAL_TYPES,
  credentialStatus,
  credentialsForWorker,
  reviewCredential,
} from '../lib/credentials'

export function Onboarding() {
  const { allUsers, currentUser } = useApp()

  if (currentUser.role === 'support_worker') {
    return <WorkerOnboarding userId={currentUser.id} />
  }

  return <OnboardingRecords workers={allUsers.filter((user) => user.role === 'support_worker')} />
}

function WorkerOnboarding({ userId }: { userId: string }) {
  const [record, setRecord] = useState(() => getOnboardingRecord(userId))
  const progress = onboardingProgress(record)
  const canComplete = progress === 100 && record.acknowledged && record.signedName.trim().length > 1

  const update = (patch: Partial<WorkerOnboardingRecord>) => {
    const next = saveOnboardingRecord({ ...record, ...patch, userId })
    setRecord(next)
  }

  const toggleItem = (id: string) => {
    const completedItemIds = record.completedItemIds.includes(id)
      ? record.completedItemIds.filter((itemId) => itemId !== id)
      : [...record.completedItemIds, id]
    update({ completedItemIds, completedAt: undefined })
  }

  const complete = () => {
    if (!canComplete) return
    update({ completedAt: new Date().toISOString() })
  }

  return (
    <div>
      <TopBar title="My Onboarding" subtitle="Participant-specific information for your support role" />
      <div className="flex flex-col gap-4 px-4 py-4">
        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-slate-900">Onboarding progress</p>
              <p className="text-xs text-slate-500">{record.completedItemIds.length} of {ONBOARDING_REQUIREMENTS.length} completed</p>
            </div>
            <div className="text-2xl font-bold text-brand-600">{progress}%</div>
          </div>
          <div className="mt-3 h-3 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-brand-500" style={{ width: `${progress}%` }} /></div>
        </section>

        <section className="flex flex-col gap-2">
          {ONBOARDING_REQUIREMENTS.map((item) => {
            const completed = record.completedItemIds.includes(item.id)
            return (
              <button key={item.id} type="button" onClick={() => toggleItem(item.id)} className="w-full rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-card">
                <div className="flex items-start gap-3">
                  <div className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border ${completed ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-slate-300'}`}>{completed ? '✓' : ''}</div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{item.title}</p>
                    <p className="mt-1 text-xs text-slate-500">{item.description}</p>
                    <p className="mt-2 text-xs font-semibold text-brand-600">{completed ? 'Completed' : item.category === 'Training video' ? 'Watch and mark complete' : 'Open and mark reviewed'}</p>
                  </div>
                </div>
              </button>
            )
          })}
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-card">
          <h2 className="font-semibold text-slate-900">Worker acknowledgement</h2>
          <label className="mt-3 flex items-start gap-3 text-sm text-slate-700">
            <input type="checkbox" checked={record.acknowledged} onChange={(e) => update({ acknowledged: e.target.checked, completedAt: undefined })} className="mt-1" />
            I confirm I have reviewed the participant-specific onboarding information assigned to me.
          </label>
          <input value={record.signedName} onChange={(e) => update({ signedName: e.target.value, completedAt: undefined })} placeholder="Type full name" className="mt-4 w-full rounded-xl border border-slate-200 px-3 py-3 text-sm" />
          <button type="button" onClick={complete} disabled={!canComplete} className={`mt-4 w-full rounded-xl px-4 py-3 text-sm font-semibold ${canComplete ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-400'}`}>
            {record.completedAt ? 'Onboarding completed ✓' : 'Complete onboarding'}
          </button>
          {record.completedAt && <p className="mt-2 text-center text-xs text-emerald-600">Signed {new Date(record.completedAt).toLocaleString()}</p>}
        </section>
      </div>
    </div>
  )
}

function OnboardingRecords({ workers }: { workers: ReturnType<typeof useApp>['allUsers'] }) {
  const { currentUser, allUsers } = useApp()
  const [selectedWorkerId, setSelectedWorkerId] = useState(workers[0]?.id ?? '')
  const [, forceRefresh] = useState(0)
  const records = loadOnboardingRecords()
  const selectedWorker = workers.find((worker) => worker.id === selectedWorkerId)
  const record = selectedWorker ? records.find((item) => item.userId === selectedWorker.id) ?? getOnboardingRecord(selectedWorker.id) : undefined
  const credentials = selectedWorker ? credentialsForWorker(selectedWorker.id) : []

  useEffect(() => {
    const refresh = () => forceRefresh((value) => value + 1)
    window.addEventListener('schub-onboarding-updated', refresh)
    window.addEventListener('schub-credentials-updated', refresh)
    return () => {
      window.removeEventListener('schub-onboarding-updated', refresh)
      window.removeEventListener('schub-credentials-updated', refresh)
    }
  }, [])

  const markReviewed = (credentialId: string) => {
    reviewCredential(credentialId, currentUser.id)
    forceRefresh((value) => value + 1)
  }

  return (
    <div>
      <TopBar title="Onboarding Records" subtitle="Review support worker onboarding and supplied credentials" />
      <div className="flex flex-col gap-4 px-4 py-4">
        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-card">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Support worker</p>
          <select value={selectedWorkerId} onChange={(e) => setSelectedWorkerId(e.target.value)} className="mt-3 w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm">
            {workers.map((worker) => <option key={worker.id} value={worker.id}>{worker.name}</option>)}
          </select>
          {selectedWorker && (
            <div className="mt-4 flex items-center gap-3">
              <Avatar user={selectedWorker} size={48} />
              <div><p className="font-semibold text-slate-900">{selectedWorker.name}</p><p className="text-xs text-slate-500">{selectedWorker.title ?? roleLabels[selectedWorker.role]}</p></div>
            </div>
          )}
        </section>

        {record && (
          <>
            <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-card">
              <div className="flex items-center justify-between">
                <div><p className="font-semibold text-slate-900">Onboarding completion</p><p className="text-xs text-slate-500">{record.completedItemIds.length} of {ONBOARDING_REQUIREMENTS.length} requirements completed</p></div>
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${record.completedAt ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{record.completedAt ? 'Completed' : 'In progress'}</span>
              </div>
              <p className="mt-3 text-sm text-slate-700">Acknowledgement: <strong>{record.acknowledged ? 'Yes' : 'No'}</strong></p>
              <p className="mt-1 text-sm text-slate-700">Signed name: <strong>{record.signedName || 'Not signed'}</strong></p>
              {record.completedAt && <p className="mt-1 text-xs text-slate-500">Completed {new Date(record.completedAt).toLocaleString()}</p>}
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-card">
              <h2 className="font-semibold text-slate-900">Onboarding requirements</h2>
              <div className="mt-3 flex flex-col gap-2">
                {ONBOARDING_REQUIREMENTS.map((item) => {
                  const complete = record.completedItemIds.includes(item.id)
                  return <div key={item.id} className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2"><span className="text-sm text-slate-700">{item.title}</span><span className={`text-xs font-semibold ${complete ? 'text-emerald-600' : 'text-slate-400'}`}>{complete ? '✓ Reviewed' : 'Pending'}</span></div>
                })}
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-card">
              <h2 className="font-semibold text-slate-900">Worker credentials</h2>
              <p className="mt-1 text-xs leading-5 text-slate-500">These are documents/details supplied by the worker. “Reviewed” means an authorised person has visually checked the supplied information; Marnixia is not independently validating the credential.</p>
              <div className="mt-3 flex flex-col gap-2">
                {CREDENTIAL_TYPES.map((meta) => {
                  const credential = credentials.find((item) => item.type === meta.type)
                  const reviewer = credential?.reviewedByUserId ? allUsers.find((user) => user.id === credential.reviewedByUserId) : undefined
                  return (
                    <div key={meta.type} className="rounded-xl bg-slate-50 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-slate-800">{meta.label}</p>
                          <p className="text-xs text-slate-500">{credential ? credentialStatus(credential) : 'Missing'}</p>
                          {credential?.fileName && <p className="mt-1 text-xs text-slate-500">📄 {credential.fileName}</p>}
                          {credential?.referenceNumber && <p className="mt-1 text-xs text-slate-500">Reference: {credential.referenceNumber}</p>}
                          {credential?.expiryDate && <p className="mt-1 text-xs text-slate-500">Expiry: {new Date(`${credential.expiryDate}T00:00:00`).toLocaleDateString()}</p>}
                          {credential?.reviewedAt && <p className="mt-1 text-xs font-semibold text-emerald-600">Reviewed by {reviewer?.name ?? 'authorised reviewer'} · {new Date(credential.reviewedAt).toLocaleDateString()}</p>}
                        </div>
                        {credential && !credential.reviewedAt && (
                          <button onClick={() => markReviewed(credential.id)} className="rounded-lg bg-brand-600 px-3 py-2 text-xs font-semibold text-white">Mark reviewed</button>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  )
}

